import React from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useFooterContext } from 'Context/FooterContext';
import BaseFormLayout from 'Components/ComponentLibrary/Templates/Unauthenticated/Content/BaseFormLayout';
import Portal from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Portal';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import TextButton from 'Components/ComponentLibrary/Components/Uncategorized/TextButton';
import fr from 'facet-rest';
import { pickFunction } from 'Helpers/Affordances';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useSetNewPasswordContext } from '../../../SetNewPasswordContext';

const styles = (theme) => ({
  root: {},
  secondaryButton: {
    marginTop: theme.spacing(2),
  },
});

const MultiFactorAuth = ({
  className,
  classes,
}) => {
  const {
    resource, unsourcedErrors, onSubmit,
  } = useMetaLayoutContext();
  const { footerRef } = useFooterContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const { handleSubmit } = useSetNewPasswordContext();

  const loginViaCode = async event => {
    if (event) {
      event.preventDefault();
    }
    const coll = await onSubmit();
    if (coll) {
      await handleSubmit(coll);
    }
  };

  const handleResend = async event => {
    if (event) {
      event.preventDefault();
    }
    try {
      resource.attr('resendCode', 'true');
      const coll = await resource.submit();
      switch (coll.meta().type()) {
        case 'AuthPasswordResetMultiFactor':
          displaySuccess('Verification code sent');
          break;
          /* no default */
      }
      await handleSubmit(coll);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const switchToAuthenticatorApp = async event => {
    if (event) {
      event.preventDefault();
    }

    const [mfaPreferenceRes] = await resource.relationship(fr.relationships.mfaPreference.name).link('related').get();
    mfaPreferenceRes.attr('preference', 'totp');
    const coll = await mfaPreferenceRes.submit();
    if (coll) {
      await handleSubmit(coll);
    }
  };

  const renderTextButton = () => {
    return pickFunction(resource, {
      [fr.relationships.mfaPreference.name]: () => {
        return (
          <TextButton
            className={classes.secondaryButton}
            disableElevation
            color={'primary'}
            variant={'underlined'}
            fullWidth
            onClick={switchToAuthenticatorApp}
            test={'switch-to-authenticator-app'}
          >
            {'Enter code from authenticator app'}
          </TextButton>
        );
      },
      [fr.relationships.default.name]: () => {
        return (
          <TextButton
            className={classes.secondaryButton}
            onClick={handleResend}
            test={'resend-code-button'}
            fullWidth
          >
            {'Send code again'}
          </TextButton>
        );
      },
    });
  };

  return (
    <BaseFormLayout
      className={classNames(classes.root, className)}
      onSubmit={loginViaCode}
      id={'multi-factor-authentication-form'}
      title={'Password Reset'}
      subtitle={`Enter the six digit code we texted to ${resource.attr('sentTo')}`}
      errors={unsourcedErrors}
      message={resource.attr('message')}
    >
      <MetaField
        attr={'code'}
        inputProps={{
          test: 'sms-code-field',
        }}
      />

      <Portal container={footerRef.current}>
        <RoundedButton
          onClick={loginViaCode}
          test={'submit-sms-continue-to-password-reset-button'}
          type={'submit'}
          form={'multi-factor-authentication-form'}
          fullWidth
        >
          {'Continue'}
        </RoundedButton>
        {renderTextButton()}
      </Portal>
    </BaseFormLayout>
  );
};

MultiFactorAuth.defaultProps = {
  className: '',
};

MultiFactorAuth.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default withStyles(styles)(MultiFactorAuth);
